import React, { useEffect, useRef, useState } from 'react'
import { Card, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { getCustomerSyncStatus, runCustomerSync, startCustomerSyncJob, getCustomerSyncProgress, SyncResult, SyncStatus, SyncJobProgress } from './crmApi'

/**
 * WeQuote data sync — pull WeQuote's CURRENT customer data into the CRM. Manual, deliberate,
 * two-step, and covering every account status (live, trial, expired, cancelled), each landing on
 * the matching pipeline stage.
 *
 * ALWAYS PREVIEW FIRST: a dry run that writes nothing and reports what would change. Apply then
 * runs as a BACKGROUND JOB on the server — no browser/gateway timeout can cut it off mid-run (the
 * cause of earlier partial syncs) — with a live progress bar here. A sync writes only the fields
 * WeQuote owns; owner, score, tags, notes and everything the sales team typed are never touched.
 */

const ACTION_STYLE: Record<string, { label: string; bg: string; fg: string }> = {
  create: { label: 'New', bg: '#DCFCE7', fg: '#166534' },
  update: { label: 'Update', bg: '#DBEAFE', fg: '#1E40AF' },
  unchanged: { label: 'No change', bg: '#F1F5F9', fg: '#64748B' },
  skip: { label: 'Skipped', bg: '#FEF3C7', fg: '#92400E' },
}

export const CrmCustomerSync: React.FC = () => {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [preview, setPreview] = useState<SyncResult | null>(null)
  const [busy, setBusy] = useState<'preview' | null>(null)
  const [jobState, setJobState] = useState<SyncJobProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = () => { getCustomerSyncStatus().then(setStatus).catch(() => setStatus(null)) }
  useEffect(refresh, [])

  // If a job is already running (e.g. page reopened mid-sync), pick it up.
  useEffect(() => {
    getCustomerSyncProgress().then((j) => { if (j.running) { setJobState(j); startPolling() } }).catch(() => {})
    return stopPolling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }
  const startPolling = () => {
    stopPolling()
    pollRef.current = setInterval(async () => {
      try {
        const j = await getCustomerSyncProgress()
        setJobState(j)
        if (!j.running) {
          stopPolling()
          if (j.phase === 'done' && j.result) {
            crmToast(`Sync complete: ${j.result.created} created, ${j.result.updated} updated, ${j.result.unchanged} unchanged`, 'success')
            setPreview(null)
            refresh()
          } else if (j.phase === 'error') {
            const lockBusy = /already in progress|already running/i.test(j.error || '')
            crmToast(j.error || 'Sync failed', lockBusy ? 'info' : 'error')
          }
        }
      } catch { /* transient poll failure — keep polling */ }
    }, 1200)
  }

  const runPreview = async () => {
    setBusy('preview')
    setError(null)
    try {
      const r = await runCustomerSync({ dryRun: true })
      setPreview(r)
      crmToast(`Preview: ${r.created} new, ${r.updated} to update, ${r.unchanged} unchanged`, 'success')
    } catch (e) {
      const msg = (e as Error).message || 'Preview failed'
      setError(msg)
      crmToast(msg, 'error')
    } finally {
      setBusy(null)
    }
  }

  const runApply = async () => {
    setError(null)
    try {
      const r = await startCustomerSyncJob()
      if (r.started) {
        setJobState(r.job || { running: true, phase: 'pulling' })
        startPolling()
      } else {
        crmToast('A sync is already running', 'info')
        startPolling()
      }
    } catch (e) {
      const msg = (e as Error).message || 'Could not start the sync'
      setError(msg)
      crmToast(msg, 'error')
    }
  }

  const configured = !!status?.configured
  const last = status?.lastSync
  const jobRunning = !!jobState?.running
  // "Error" that only means the background auto-sync already holds the lock — a healthy state,
  // rendered as neutral info rather than an alarming red failure.
  const lockBusy = jobState?.phase === 'error' && /already in progress|already running/i.test(jobState.error || '')
  const pct = jobState?.total ? Math.min(100, Math.round(((jobState.processed || 0) / jobState.total) * 100)) : 0
  const doneResult = !jobRunning && jobState?.phase === 'done' ? jobState.result : null
  const cov = doneResult?.coverage || preview?.coverage

  return (
    <Card
      title="WeQuote data sync"
      subtitle="Pull WeQuote's current customer data — every status (live, trial, expired, cancelled) lands on its matching stage. Auto-syncs every couple of minutes; the buttons force it now."
    >
      {!configured && (
        <div className="rounded-lg p-3 mb-3" style={{ background: '#FEF3C7' }}>
          <p className="text-[12px] font-semibold" style={{ color: '#92400E' }}>Waiting on the WeQuote customers API</p>
          <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#92400E' }}>
            Set {status?.needs?.join(' and ') || 'WEQUOTE_CUSTOMERS_URL'} on the server — the buttons go live the moment they're set.
          </p>
        </div>
      )}

      <p className="text-[12px] text-txt-secondary leading-relaxed">
        A sync writes <span className="font-semibold">only what WeQuote owns</span> — account id, plan, status→stage
        (forward only), activation and trial dates. Owner, score, tags, qualification, tasks, notes and existing
        contacts are never overwritten. Matching is by account id, then contact email — never by company name.
      </p>

      {last && !jobRunning && (
        <p className="text-[11px] text-txt-tertiary mt-2">
          Last sync {new Date(last.ranAt).toLocaleString()} — {last.created} created, {last.updated} updated,{' '}
          {last.unchanged} unchanged{last.skipped ? `, ${last.skipped} skipped` : ''}.
        </p>
      )}

      {/* Progress bar — pinned while the background job runs. */}
      {jobState && (jobRunning || jobState.phase === 'done' || jobState.phase === 'error') && (
        <div className="mt-3 rounded-xl border border-border p-3">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-[12px] font-semibold text-txt-primary">
              {jobRunning
                ? jobState.phase === 'pulling' ? 'Pulling current data from WeQuote…' : `Syncing ${jobState.processed ?? 0} of ${jobState.total ?? '…'}`
                : jobState.phase === 'done' ? 'Sync complete' : lockBusy ? 'Sync already in progress' : 'Sync failed'}
            </p>
            <p className="text-[11px] text-txt-tertiary">{jobRunning && jobState.total ? `${pct}%` : ''}</p>
          </div>
          <div className="h-2 rounded-full bg-surface-tertiary overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: jobRunning ? (jobState.phase === 'pulling' ? '6%' : `${pct}%`) : '100%',
                background: jobState.phase === 'error' ? (lockBusy ? '#d97706' : '#dc2626') : jobState.phase === 'done' ? '#16a34a' : '#2563eb',
              }}
            />
          </div>
          {doneResult && (
            <p className="text-[11px] text-txt-secondary mt-2">
              {doneResult.created} created · {doneResult.updated} updated · {doneResult.unchanged} unchanged
              {doneResult.skipped ? ` · ${doneResult.skipped} skipped` : ''}
              {doneResult.archived ? ` · ${doneResult.archived} archived (account gone from WeQuote)` : ''} — of {doneResult.total} pulled.
            </p>
          )}
          {jobState.phase === 'error' && <p className="text-[11px] mt-2" style={{ color: lockBusy ? '#d97706' : '#dc2626' }}>{jobState.error}</p>}
        </div>
      )}

      {/* Field coverage — proves what WeQuote actually sent, so "Unknown" dates are traceable. */}
      {cov && (
        <p className="text-[11px] text-txt-tertiary mt-2">
          WeQuote sent an activation date for <span className="font-semibold">{cov.activatedParseable}</span> of{' '}
          {preview?.total || doneResult?.total} accounts
          {cov.activatedSent !== cov.activatedParseable ? ` (${cov.activatedSent - cov.activatedParseable} unparseable)` : ''}
          {' '}· trial start {cov.trialStartSent} · trial end {cov.trialEndSent}. Accounts without a date show “Unknown”.
        </p>
      )}

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <Button size="sm" onClick={runPreview} disabled={!configured || busy !== null || jobRunning}>
          {busy === 'preview' ? 'Checking…' : 'Preview sync'}
        </Button>
        <Button size="sm" onClick={runApply} disabled={!configured || busy !== null || jobRunning}>
          {jobRunning ? 'Syncing…' : 'Sync now'}
        </Button>
        {preview && <button className="text-[11px] text-txt-tertiary underline" onClick={() => setPreview(null)}>Discard preview</button>}
      </div>

      {error && <p className="text-[11px] mt-2" style={{ color: '#dc2626' }}>{error}</p>}

      {preview && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">
            Preview — nothing has been written ({preview.total} account{preview.total === 1 ? '' : 's'} from WeQuote)
          </p>
          {preview.created === 0 && preview.updated === 0 ? (
            <p className="text-xs text-txt-tertiary">Everything is already up to date — a sync would change nothing.</p>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden max-h-[320px] overflow-y-auto">
              {preview.rows
                .filter((r) => r.action !== 'unchanged')
                .slice(0, 200)
                .map((r, i) => {
                  const s = ACTION_STYLE[r.action]
                  return (
                    <div key={`${r.accountId}-${i}`} className="flex items-center gap-2.5 px-3 py-2 border-b border-border last:border-0">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                      <span className="text-[13px] text-txt-primary truncate flex-1">{r.company}</span>
                      <span className="text-[11px] text-txt-tertiary truncate max-w-[45%] text-right">
                        {r.changes?.join(', ') || r.reason || ''}
                      </span>
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
